<template>
  <div class="tw-w-2/3" v-show="!isPageHidden">
    <template
      v-for="(form, simpleFormIndex) in forms"
      :key="`${recapType}-${taskOrStoreId}-${(form as Form).id}`"
    >
      <recap-form
        v-if="(form as Form).form_type === 'simple'"
        :ref="(getFormRef as any)"
        :form="form"
        :repeating-index="simpleFormIndex"
      >
      </recap-form>
      <template v-else-if="(form as Form).form_type === 'product'">
        <template
          v-for="(product, repeatingIndex) in (form as Form).products"
          :key="`${recapType}-${taskOrStoreId}-${(form as Form).id}-${repeatingIndex}-${product.id}`"
        >
          <recap-form
            :ref="(getFormRef as any)"
            :form="form"
            :product="product"
            :repeating-index="repeatingIndex"
          >
          </recap-form>
        </template>
      </template>
      <template v-else-if="(form as Form).form_type === 'survey'">
        <template
          v-for="(surveyEntry, repeatingIndex) in surveyEntries((form as Form).id)"
          :key="`${recapType}-${taskOrStoreId}-${(form as Form).id}-${repeatingIndex}`"
        >
          <recap-form
            :ref="(getFormRef as any)"
            :form="form"
            :repeating-index="repeatingIndex"
            :last-survey="
              repeatingIndex !== 0 &&
              repeatingIndex + 1 === surveyEntries((form as Form).id).length
            "
            @add-survey="addSurveyEntry((form as Form).id)"
            @remove-survey="removeSurveyEntry((form as Form).id, repeatingIndex)"
          >
          </recap-form>
        </template>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Form, Page } from '../../types/recap'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import RecapForm from './RecapForm.vue'
import { useSharedRecap } from '../../composables/recaps/useRecap'
import { Nullish } from '../../types/utility'

const { getFormRef, getPageById, triggerAllConditions, hiddenPages, taskOrStoreId, recapType } =
  useSharedRecap()

const props = defineProps<{ pageId: string }>()

const currentPage = ref<Page | Nullish>(getPageById(props.pageId))
watch(
  () => props.pageId,
  () => {
    if (props.pageId !== currentPage.value?.id) {
      currentPage.value = getPageById(props.pageId)
    }
  }
)

const isPageHidden = computed(() => !hiddenPages.value.includes(props.pageId))

const forms = computed(() => currentPage.value?.items || currentPage.value?.forms || [])

const allSurveyEntries = reactive<{ [key: string]: string[] }>({})
function surveyEntries(formId: string) {
  let entriesForForm = allSurveyEntries[formId]
  if (!entriesForForm) {
    entriesForForm = [formId]
    allSurveyEntries[formId] = entriesForForm
  }
  return entriesForForm
}
function addSurveyEntry(formId: string) {
  allSurveyEntries[formId].push(new Date().toString())

  // Trigger all logic conditions when adding a survey form
  // so all the fields within are properly shown
  // Must happen after the form has completely rendered, hence the `nextTick()`
  nextTick(() => {
    triggerAllConditions.value = true
  }).catch(() => null)
}
function removeSurveyEntry(formId: string, index: number) {
  if (allSurveyEntries[formId].length > 1) {
    allSurveyEntries[formId].splice(index, 1)
  }
}

function getPageId() {
  return props.pageId
}

defineExpose({
  getPageId,
})
</script>
